import { Card } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";

interface ContactSectionProps {
  profile?: {
    email?: string;
    phone_number?: string;
    linkedin_url?: string;
    website_url?: string;
    github_url?: string;
    instagram_url?: string;
    facebook_url?: string;
    facebook_page_url?: string;
    twitter_url?: string;
    dribbble_url?: string;
    behance_url?: string;
    medium_url?: string;
    discord_username?: string;
    slack_url?: string;
    whatsapp_number?: string;
    telegram_username?: string;
    youtube_url?: string;
    tiktok_url?: string;
    pinterest_url?: string;
  } | null;
}

export const ContactSection = ({ profile }: ContactSectionProps) => {
  if (!profile) return null;

  const contactItems = [
    { value: profile.email, icon: "email", label: "Email", href: `mailto:${profile.email}`, display: profile.email },
    { value: profile.phone_number, icon: "phone", label: "Phone", href: `tel:${profile.phone_number}`, display: profile.phone_number },
    { value: profile.whatsapp_number, icon: "whatsapp", label: "WhatsApp", href: `https://wa.me/${profile.whatsapp_number?.replace(/\D/g, '')}`, display: profile.whatsapp_number },
    { value: profile.linkedin_url, icon: "linkedin", label: "LinkedIn", href: profile.linkedin_url, display: profile.linkedin_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.github_url, icon: "github", label: "GitHub", href: profile.github_url, display: profile.github_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.website_url, icon: "language", label: "Website", href: profile.website_url, display: profile.website_url?.replace(/^https?:\/\//, "") },
    { value: profile.instagram_url, icon: "instagram", label: "Instagram", href: profile.instagram_url, display: profile.instagram_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.facebook_url, icon: "facebook", label: "Facebook", href: profile.facebook_url, display: profile.facebook_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.facebook_page_url, icon: "business", label: "Facebook Page", href: profile.facebook_page_url, display: profile.facebook_page_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.twitter_url, icon: "twitter", label: "X (Twitter)", href: profile.twitter_url, display: profile.twitter_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.dribbble_url, icon: "dribbble", label: "Dribbble", href: profile.dribbble_url, display: profile.dribbble_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.behance_url, icon: "behance", label: "Behance", href: profile.behance_url, display: profile.behance_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.medium_url, icon: "medium", label: "Medium", href: profile.medium_url, display: profile.medium_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.youtube_url, icon: "youtube", label: "YouTube", href: profile.youtube_url, display: profile.youtube_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.tiktok_url, icon: "tiktok", label: "TikTok", href: profile.tiktok_url, display: profile.tiktok_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.pinterest_url, icon: "pinterest", label: "Pinterest", href: profile.pinterest_url, display: profile.pinterest_url?.replace(/^https?:\/\/(www\.)?/, "") },
    { value: profile.telegram_username, icon: "telegram", label: "Telegram", href: `https://t.me/${profile.telegram_username?.replace('@', '')}`, display: profile.telegram_username },
    { value: profile.discord_username, icon: "discord", label: "Discord", href: undefined, display: profile.discord_username },
    { value: profile.slack_url, icon: "slack", label: "Slack", href: profile.slack_url, display: profile.slack_url?.replace(/^https?:\/\/(www\.)?/, "") },
  ].filter(item => item.value);

  if (contactItems.length === 0) return null;

  return (
    <section id="contact" className="section-padding bg-card">
      <div className="max-w-6xl mx-auto container-padding">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 animate-fade-in">
          Get In Touch
        </h2>
        <p className="text-muted-foreground mb-10 animate-fade-in animation-delay-100">
          Whether it's a new role, collaboration, or a quick question, feel free to reach out.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contactItems.map((item, index) => (
            <Card 
              key={index} 
              className="p-4 hover:shadow-md transition-all duration-200 animate-fade-in"
              style={{ animationDelay: `${(index + 1) * 50}ms` }}
            >
              <div className="flex items-start gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary flex-shrink-0">
                  <Icon name={item.icon} size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-foreground text-sm mb-0.5">{item.label}</h3>
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? "_blank" : undefined}
                      rel={item.href.startsWith('http') ? "noopener noreferrer" : undefined}
                      className="text-primary hover:underline text-sm break-all leading-relaxed"
                    >
                      {item.display}
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-sm break-all">{item.display}</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
